/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import React, {useEffect, useRef, useState} from 'react';
import Snackbar from "./snackbar";
import {Snack} from "../util/interfaces";
import delay from 'lodash.delay';
import defer from 'lodash.defer';
import {useEvent} from "../util/componentUtil";

interface Props {
    activeTime?: number;
    backgroundColor?: string;
    textColor?: string;
    maxSnacks?: number;
    noDuplicates?: boolean;
    containerClass?: string;
    textClass?: string;
}

export default function SnackbarController(props: Props) {
    const {containerClass, textClass, textColor, backgroundColor, noDuplicates, maxSnacks, activeTime} = props;
    const [active, setActive] = useState(false);
    const [snack, setSnack] = useState<Snack | null>(null);
    const snackQueue = useRef<Array<Snack>>([]);
    const timeoutID = useRef<number | null>(null);
    useEvent('onShowSnackbar', onAddSnack);

    function onAddSnack(snack: Snack) {
        if (noDuplicates && isDuplicate(snack)) return;
        snackQueue.current.push(snack);
        if (maxSnacks !== undefined)
            while (snackQueue.current.length > maxSnacks - 1)
                snackQueue.current.shift();
        if (!active) activate();
    }

    function onClick() {
        clearTimeout();
        deactivate();
    }

    function isDuplicate(_snack: Snack): boolean {
        return snack?.text === _snack.text || snackQueue.current.pop()?.text === _snack.text;
    }

    function activate() {
        const snack: Snack | undefined = snackQueue.current.pop();
        if (!snack) return;
        const _activeTime = snack.config?.duration ?? activeTime ?? 3000;
        setSnack(snack);
        defer(() => setActive(true));
        timeoutID.current = window.setTimeout(deactivate, _activeTime);
    }

    function deactivate() {
        setActive(false);
        // Clear snack after 200ms (css transition)
        delay(() => {
            setSnack(null);
            defer(() => {
                if (snackQueue.current.length)
                    // Open next
                    timeoutID.current = window.setTimeout(activate, 500);
            })
        }, 200);
    }

    function clearTimeout() {
        if (timeoutID.current)
            window.clearTimeout(timeoutID.current);
        timeoutID.current = null;
    }

    useEffect(() => {
        return function () {
            clearTimeout();
        }
    }, []);

    return (
        <Snackbar snack={snack} containerClass={containerClass} textClass={textClass} active={active} onClick={onClick}
                  textColor={textColor} backgroundColor={backgroundColor}/>
    );
}